import { apiSlice } from './apiSlice';
import type {
  Save,
  ListSavesResponse,
  MoveCursorRequest,
  MoveCursorResponse,
  CloneSaveRequest,
  CloneSaveResponse,
  CloneJob,
  RefreshCapabilitiesResponse,
  ScenarioTemplate,
  ListScenariosResponse,
  StartScenarioRequest,
  StartScenarioResponse,
  SaveType,
  SaveStatus,
  ScenarioKind,
  ScenarioVisibility,
  GetSaveComparisonResponse,
  SaveWorld,
} from '../types/saves';
import type {
  WeatherMapItem,
  UserClimateMap,
  WeatherImportLocationsResponse,
} from '../types/simulation';

export const savesV2Api = apiSlice
  .enhanceEndpoints({ addTagTypes: ['SaveV2', 'ScenarioTemplate'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      listSaves: builder.query<
        Save[],
        { type?: SaveType; status?: SaveStatus; limit?: number }
      >({
        query: (args = {}) => {
          const params = new URLSearchParams();
          if (args.type) params.set('type', args.type);
          if (args.status) params.set('status', args.status);
          if (args.limit) params.set('limit', String(args.limit));
          const qs = params.toString();
          return `/saves${qs ? `?${qs}` : ''}`;
        },
        transformResponse: (resp: ListSavesResponse) => resp.saves,
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'SaveV2' as const, id })),
                { type: 'SaveV2', id: 'LIST' },
              ]
            : [{ type: 'SaveV2', id: 'LIST' }],
      }),

      getSaveComparison: builder.query<GetSaveComparisonResponse, string>({
        query: (saveId) => `/saves/${saveId}/comparison`,
        providesTags: (_r, _e, saveId) => [{ type: 'SaveV2', id: `comparison-${saveId}` }],
      }),

      // GET /api/saves/:saveId/world — единый снимок мира (free + scenario).
      // Management consumers can opt into future plantings and/or inactive
      // structural objects without changing the default game/Unity snapshot.
      getSaveWorld: builder.query<SaveWorld, string | {
        saveId: string;
        includeFuturePlantings?: boolean;
        includeInactiveStructuralObjects?: boolean;
      }>({
        query: (arg) => {
          const saveId = typeof arg === 'string' ? arg : arg.saveId;
          const includeFuture = typeof arg === 'object' && arg.includeFuturePlantings === true;
          const includeInactiveStructures = typeof arg === 'object'
            && arg.includeInactiveStructuralObjects === true;
          const query = new URLSearchParams();
          if (includeFuture) query.set('includeFuturePlantings', '1');
          if (includeInactiveStructures) query.set('includeInactiveStructuralObjects', '1');
          const suffix = query.toString();
          return `/saves/${saveId}/world${suffix ? `?${suffix}` : ''}`;
        },
        providesTags: (_r, _e, arg) => {
          const saveId = typeof arg === 'string' ? arg : arg.saveId;
          return [
            { type: 'SaveV2', id: saveId },
            { type: 'SaveV2', id: `world-${saveId}` },
          ];
        },
      }),

      createFreeSave: builder.mutation<any, Record<string, unknown>>({
        query: (body) => ({
          url: '/saves',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'SaveV2', id: 'LIST' }],
      }),

      listSystemWeatherMaps: builder.query<WeatherMapItem[], void>({
        query: () => '/simulation/weather/system-maps',
        transformResponse: (resp: WeatherMapItem[]) => Array.isArray(resp) ? resp : [],
      }),

      // Guided creation §B: catalog of selectable growing locations + recent years.
      listWeatherImportLocations: builder.query<WeatherImportLocationsResponse, void>({
        query: () => '/simulation/weather/import/locations',
        transformResponse: (resp: WeatherImportLocationsResponse) => ({
          locations: Array.isArray(resp?.locations) ? resp.locations : [],
          recentCompleteYears: Array.isArray(resp?.recentCompleteYears) ? resp.recentCompleteYears : [],
        }),
      }),

      // Guided creation §F: the user's reusable custom climate maps (no farm yet),
      // optionally filtered to the chosen city's geo.
      listUserClimateMaps: builder.query<UserClimateMap[], { lat?: number; lon?: number } | void>({
        query: (args) => {
          const params = new URLSearchParams();
          if (args && args.lat != null && args.lon != null) {
            params.set('lat', String(args.lat));
            params.set('lon', String(args.lon));
          }
          const qs = params.toString();
          return `/simulation/weather/user-maps${qs ? `?${qs}` : ''}`;
        },
        transformResponse: (resp: UserClimateMap[]) => (Array.isArray(resp) ? resp : []),
      }),

      // PATCH /api/saves/:saveId — edit save metadata (name / coords / description).
      // Canonical name/coords/description live on the linked farm container.
      updateSave: builder.mutation<
        { save: Save },
        { saveId: string; name?: string; locationCoords?: string | null; description?: string | null }
      >({
        query: ({ saveId, ...body }) => ({
          url: `/saves/${saveId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_r, _e, { saveId }) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SaveV2', id: `world-${saveId}` },
          { type: 'SaveV2', id: 'LIST' },
        ],
      }),

      deleteSave: builder.mutation<{ save: Save; message?: string }, string>({
        query: (saveId) => ({
          url: `/saves/${saveId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, saveId) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SaveV2', id: `world-${saveId}` },
          { type: 'SaveV2', id: `comparison-${saveId}` },
          { type: 'SaveV2', id: 'LIST' },
        ],
      }),

      moveSaveCursor: builder.mutation<
        MoveCursorResponse,
        { saveId: string; body: MoveCursorRequest }
      >({
        query: ({ saveId, body }) => ({
          url: `/saves/${saveId}/cursor`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (result, _e, { saveId }) => {
          // Don't invalidate when 409 requires_confirmation came back as 200/409
          // (RTK Query treats 409 as error → invalidatesTags isn't called).
          if (result && (result as { status?: string }).status === 'requires_confirmation') {
            return [];
          }
          return [
            { type: 'SaveV2', id: saveId },
            { type: 'SaveV2', id: `world-${saveId}` },
            { type: 'SaveV2', id: `comparison-${saveId}` },
            { type: 'SaveV2', id: 'LIST' },
          ];
        },
      }),

      // Async clone — returns `{ cloneJobId, status: 'queued', ... }`.
      // The target save id only exists once the job reports
      // `status: 'succeeded'`; poll via `getCloneJob` below.
      cloneSave: builder.mutation<
        CloneSaveResponse,
        { saveId: string; body: CloneSaveRequest }
      >({
        query: ({ saveId, body }) => ({
          url: `/saves/${saveId}/clone`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_r, _e, { saveId }) => [
          { type: 'SaveV2', id: `clone-jobs-${saveId}` },
        ],
      }),

      getCloneJob: builder.query<
        CloneJob,
        { saveId: string; jobId: string }
      >({
        query: ({ saveId, jobId }) => `/saves/${saveId}/clone-jobs/${jobId}`,
        providesTags: (_r, _e, { saveId, jobId }) => [
          { type: 'SaveV2', id: `clone-job-${saveId}-${jobId}` },
        ],
      }),

      refreshSaveCapabilities: builder.mutation<RefreshCapabilitiesResponse, string>({
        query: (saveId) => ({
          url: `/saves/${saveId}/refresh-capabilities`,
          method: 'POST',
        }),
        invalidatesTags: (_r, _e, saveId) => [
          { type: 'SaveV2', id: saveId },
          { type: 'SaveV2', id: `comparison-${saveId}` },
          { type: 'SaveV2', id: 'LIST' },
        ],
      }),

      listScenarios: builder.query<
        ScenarioTemplate[],
        { kind?: ScenarioKind; visibility?: ScenarioVisibility; limit?: number }
      >({
        query: (args = {}) => {
          const params = new URLSearchParams();
          if (args.kind) params.set('kind', args.kind);
          if (args.visibility) params.set('visibility', args.visibility);
          if (args.limit) params.set('limit', String(args.limit));
          const qs = params.toString();
          return `/scenarios${qs ? `?${qs}` : ''}`;
        },
        transformResponse: (resp: ListScenariosResponse) => resp.scenarios,
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ scenarioTemplateId }) => ({
                  type: 'ScenarioTemplate' as const,
                  id: scenarioTemplateId,
                })),
                { type: 'ScenarioTemplate', id: 'LIST' },
              ]
            : [{ type: 'ScenarioTemplate', id: 'LIST' }],
      }),

      getScenarioTemplate: builder.query<ScenarioTemplate, { templateId: string; version?: number }>({
        query: ({ templateId, version }) => {
          const params = new URLSearchParams();
          if (version != null) params.set('version', String(version));
          const qs = params.toString();
          return `/scenarios/${templateId}${qs ? `?${qs}` : ''}`;
        },
        transformResponse: (resp: { scenario?: ScenarioTemplate } | ScenarioTemplate) =>
          ((resp as { scenario?: ScenarioTemplate }).scenario ?? resp) as ScenarioTemplate,
        providesTags: (_r, _e, { templateId }) => [{ type: 'ScenarioTemplate', id: templateId }],
      }),

      startScenario: builder.mutation<
        StartScenarioResponse,
        { templateId: string; body?: StartScenarioRequest }
      >({
        query: ({ templateId, body }) => ({
          url: `/scenarios/${templateId}/start`,
          method: 'POST',
          body: body ?? {},
        }),
        invalidatesTags: [{ type: 'SaveV2', id: 'LIST' }],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useListSavesQuery,
  useGetSaveComparisonQuery,
  useGetSaveWorldQuery,
  useCreateFreeSaveMutation,
  useListSystemWeatherMapsQuery,
  useListWeatherImportLocationsQuery,
  useListUserClimateMapsQuery,
  useUpdateSaveMutation,
  useDeleteSaveMutation,
  useMoveSaveCursorMutation,
  useCloneSaveMutation,
  useGetCloneJobQuery,
  useRefreshSaveCapabilitiesMutation,
  useListScenariosQuery,
  useGetScenarioTemplateQuery,
  useStartScenarioMutation,
} = savesV2Api;

export default savesV2Api;
